import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { ContentCard } from 'components/Card/ContentCard';
import { CenterPositionedRow } from 'components/flex-containers';
import { BoldLabel, LightGrayLabel } from 'components/labels';
import { NotFoundErrorScreen } from 'components/screens';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';
import {
  GENERAL_STATUS_MAP,
  GeneralStatus,
  StatusBadge
} from 'modules/status-badge';
import {
  isEffectiveValueVisible,
  mapReasonTimeMismatchType
} from 'modules/week-details';
import { getHoursAndMinutesLabel } from 'utils/date';
import { useReason } from '../hooks';
import { FloatingButtons } from './FloatingButtons';
import { ReasonDetailsSkeleton } from './ReasonDetailsSkeleton';

const StyledContentWrapper = styled(Box)(({ theme }) => ({
  paddingBottom: 16,
  '& p:nth-of-type(1)': {
    paddingBottom: 4
  },
  '& p:nth-of-type(3)': {
    color: theme.palette.primary.light
  }
}));

export const ReasonDetails = () => {
  const { reason, isLoading, isError } = useReason();

  if (isLoading) {
    return <ReasonDetailsSkeleton />;
  }

  if (isError) {
    return (
      <NotFoundErrorScreen
        title={getTrans('reasonDetailsPage.notFoundScreen.title')}
        description1={getTrans('reasonDetailsPage.notFoundScreen.description1')}
        description2={getTrans('reasonDetailsPage.notFoundScreen.description2')}
      />
    );
  }

  if (!reason) return null;

  const {
    timeMismatchType,
    status,
    reportedReasonMinutes,
    effectiveReasonMinutes,
    message,
    messageToTalent
  } = reason;

  const timeMismatchTypeLabel = mapReasonTimeMismatchType[timeMismatchType]();
  const reportedMinutesLabel = getHoursAndMinutesLabel(
    Math.abs(reportedReasonMinutes)
  );
  const effectiveMinutesLabel = isEffectiveValueVisible(
    effectiveReasonMinutes,
    reportedReasonMinutes,
    status
  )
    ? getHoursAndMinutesLabel(Math.abs(effectiveReasonMinutes ?? 0))
    : undefined;

  return (
    <Box>
      <ContentCard>
        {status && status !== GeneralStatus.REPORTED && (
          <CenterPositionedRow sx={{ pb: 1, pt: 0 }}>
            <StatusBadge status={status} statusMap={GENERAL_STATUS_MAP} />
          </CenterPositionedRow>
        )}
        <StyledContentWrapper>
          <BoldLabel>{getTrans('reasonDetailsPage.reason.title')}</BoldLabel>
          <LightGrayLabel variant="body2">
            {timeMismatchTypeLabel}
          </LightGrayLabel>
        </StyledContentWrapper>
        <StyledContentWrapper>
          <BoldLabel>{getTrans('reasonDetailsPage.duration.title')}</BoldLabel>
          <LightGrayLabel crossed={!!effectiveMinutesLabel} variant="body2">
            {reportedMinutesLabel}
          </LightGrayLabel>
          {effectiveMinutesLabel && (
            <Text variant="body2">{effectiveMinutesLabel}</Text>
          )}
        </StyledContentWrapper>
        <StyledContentWrapper>
          <BoldLabel>{getTrans('reasonDetailsPage.comment.title')}</BoldLabel>
          <LightGrayLabel data-dd-privacy="mask" variant="body2">
            {message ?? '-'}
          </LightGrayLabel>
        </StyledContentWrapper>
        {messageToTalent && (
          <StyledContentWrapper>
            <BoldLabel color="primary">
              {getTrans('reasonDetailsPage.commentByZenjob.title')}
            </BoldLabel>
            <Text data-dd-privacy="mask" variant="body2" color="primary.light">
              {messageToTalent}
            </Text>
          </StyledContentWrapper>
        )}
      </ContentCard>
      <FloatingButtons />
    </Box>
  );
};
